import { NftStakingRewardModel } from "@/api/models/nftStaking.model";
import { BigNumber } from "bignumber.js";
import BLOCKCHAIN from "@/constants/blockchain";
import { TokenTransfer } from "@multiversx/sdk-core/out";
import ELROND from "@/constants/elrond";
import xoxnoApi, { NftPrice } from "@/api/xoxnoApi";
import oneDexApi from "@/api/onedexApi";
import { NftStakingPoolInfo } from "@/contracts/nftStaking";

class NftStakingClient {
  // The backend that used to serve this pool's aggregated info is gone; everything here is now
  // sourced on-chain (see contracts/nftStaking.ts#getPoolInfo). This only combines that chain data
  // with the floor-price/token-price APR estimate, exactly as the old backend response did.
  async buildRewardModel(type: string, contractAddress: string, poolInfo: NftStakingPoolInfo): Promise<NftStakingRewardModel> {
    const rewardTokenDecimals = BLOCKCHAIN.TOKENS[poolInfo.rewardTokenId]?.decimals ?? 0;

    // Derived rather than read from the contract's own "remaining blocks" view: that view does not
    // account for an already-exhausted pool (verified on-chain: it can return a large number even
    // when accumulatedRewards === rewardCapacity), so it's computed here from the two counters that
    // are actually consistent with each other.
    const remainingCapacity = BigNumber.max(0, poolInfo.rewardCapacity.minus(poolInfo.accumulatedRewards));
    const remainingBlocks = poolInfo.perBlockRewardAmount.isGreaterThan(0)
      ? remainingCapacity.dividedToIntegerBy(poolInfo.perBlockRewardAmount, 10)
      : new BigNumber(0);
    const endBlock = new BigNumber(poolInfo.lastRewardBlockNonce).plus(remainingBlocks).toNumber();

    const rewardPerDayRaw = poolInfo.perBlockRewardAmount.multipliedBy(ELROND.BLOCKS_PER_DAY, 10);
    const rewardPerNftPerDay = poolInfo.nbNftsStaked
      ? rewardPerDayRaw.dividedToIntegerBy(poolInfo.nbNftsStaked, 10)
      : new BigNumber(0);

    let floorPrice: NftPrice | null;
    try {
      floorPrice = await xoxnoApi.getNftFloorPrice(poolInfo.farmingTokenId);
    } catch (e) {
      floorPrice = null;
    }

    let floorPriceApr: number | undefined;
    if (floorPrice) {
      if (type === BLOCKCHAIN.NFT_POOL_TYPES.VIP) {
        floorPriceApr = rewardPerNftPerDay
          .multipliedBy(36500, 10)
          .dividedBy(ELROND.EGLD_DENOMINATOR, 10)
          .dividedToIntegerBy(floorPrice.price)
          .toNumber();
      } else {
        try {
          const rewardTokenPrice = await oneDexApi.getTokenPrice(poolInfo.rewardTokenId);

          const rewardsInUsdPerDay = rewardPerDayRaw
            .multipliedBy(rewardTokenPrice)
            .dividedBy(Math.pow(10, rewardTokenDecimals), 10);

          const costInUsd = new BigNumber(poolInfo.nbNftsStaked).multipliedBy(floorPrice.usdPrice);

          floorPriceApr = rewardsInUsdPerDay.multipliedBy(36500, 10).dividedToIntegerBy(costInUsd, 10).toNumber();
        } catch (e) {
          console.error(e);
        }
      }
    }

    return {
      contractAddress,
      token: poolInfo.farmingTokenId,
      rewardToken: poolInfo.rewardTokenId,
      rewardTokenDecimals,
      metaToken: poolInfo.farmTokenId,
      rewardPerDay: TokenTransfer.fungibleFromBigInteger(poolInfo.rewardTokenId, rewardPerDayRaw, rewardTokenDecimals),
      rewardPerNftPerDay,
      rewardCapacity: poolInfo.rewardCapacity,
      nbNftsStaked: poolInfo.nbNftsStaked,
      endBlock,
      floorPriceApr,
      minimumLockBlocks: poolInfo.minimumLockBlocks,
    };
  }
}

const nftStakingClient = new NftStakingClient();

export default nftStakingClient;
